import { memo, useCallback } from 'react';
import { Paper, Typography, Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QueryGroup, QueryField, QueryRule as QueryRuleType } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import QueryRule from './QueryRule';

interface QueryRuleGroupProps {
    group: QueryGroup;
    categories: string[];
    fields: QueryField[];
    // Stable, top-level callbacks that take the group id — QueryRuleGroup curries it itself,
    // instead of the parent recreating a bound closure (and a new prop reference) on every render.
    onAddRule: (groupId: string) => void;
    onUpdateRule: (groupId: string, ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (groupId: string, ruleId: string) => void;
    onRemoveGroup?: (groupId: string) => void;
    showCombinator?: boolean;
    canRemoveGroup?: boolean;
}

const QueryRuleGroup = memo(function QueryRuleGroup({
    group,
    categories,
    fields,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onRemoveGroup,
    showCombinator = false,
    canRemoveGroup = false
}: QueryRuleGroupProps) {
    const { t } = useLanguage();
    const groupId = group.id;
    const handleAddRule = useCallback(() => onAddRule(groupId), [onAddRule, groupId]);
    const handleUpdateRule = useCallback(
        (ruleId: string, data: Partial<QueryRuleType>) => onUpdateRule(groupId, ruleId, data),
        [onUpdateRule, groupId]
    );
    const handleRemoveRule = useCallback((ruleId: string) => onRemoveRule(groupId, ruleId), [onRemoveRule, groupId]);
    const handleRemoveGroup = useCallback(() => onRemoveGroup?.(groupId), [onRemoveGroup, groupId]);
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                bgcolor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: 2
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6c757d', fontWeight: 600 }}>
                        {t('pages.clients.queryBuilder.ruleGroup')}
                    </Typography>
                    {showCombinator && (
                        <Chip
                            label={group.combinator}
                            size="small"
                            color="primary"
                            sx={{
                                fontWeight: 600,
                                bgcolor: '#556ee6',
                                color: 'white'
                            }}
                        />
                    )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddRule}
                        sx={{
                            textTransform: 'none',
                            color: '#556ee6',
                            '&:hover': {
                                backgroundColor: 'rgba(85, 110, 230, 0.08)'
                            }
                        }}
                    >
                        {t('pages.clients.queryBuilder.addRule')}
                    </Button>

                    {canRemoveGroup && onRemoveGroup && (
                        <Tooltip title={t('pages.clients.queryBuilder.deleteGroup')}>
                            <IconButton
                                size="small"
                                onClick={handleRemoveGroup}
                                sx={{
                                    color: '#dc3545',
                                    '&:hover': {
                                        backgroundColor: 'rgba(220, 53, 69, 0.1)'
                                    }
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {group.rules.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        color: '#6c757d',
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '2px dashed #dee2e6'
                    }}
                >
                    <Typography variant="body2">
                        {t('pages.clients.queryBuilder.noRulesMessage')}
                    </Typography>
                </Box>
            ) : (
                <>
                    {group.rules.map((rule, index) => (
                        <Box key={rule.id}>
                            <QueryRule
                                rule={rule}
                                categories={categories}
                                fields={fields}
                                onUpdateRule={handleUpdateRule}
                                onRemoveRule={handleRemoveRule}
                            />
                            {index < group.rules.length - 1 && (
                                <Box sx={{ textAlign: 'center', my: 1 }}>
                                    <Chip
                                        label={t('pages.clients.queryBuilder.and')}
                                        size="small"
                                        sx={{
                                            bgcolor: '#28a745',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}
                </>
            )}
        </Paper>
    );
});

export default QueryRuleGroup;
